import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../services/auth';

const ExpensesPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    amount: '',
    currency_id: '',
    description: '',
    create_date: new Date().toISOString().slice(0, 16) // YYYY-MM-DDTHH:MM
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [expensesResponse, currenciesResponse] = await Promise.all([
        apiService.expenses.getAll(),
        apiService.currencies.getAll()
      ]);

      if (expensesResponse.data.status === 'success') {
        setExpenses(expensesResponse.data.data);
      }

      if (currenciesResponse.data.status === 'success') {
        setCurrencies(currenciesResponse.data.data);
      }
    } catch (err) {
      console.error('Load data error:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        currency_id: parseInt(formData.currency_id),
        user_id: user?.id || 1
      };

      let response;
      if (editingExpense) {
        response = await apiService.expenses.update({ ...data, id: editingExpense.id });
      } else {
        response = await apiService.expenses.create(data);
      }

      if (response.data.status === 'success') {
        setSuccess(editingExpense ? 'Expense updated successfully!' : 'Expense added successfully!');
        handleCloseModal();
        loadData();
      } else {
        setError(response.data.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (expenseItem) => {
    setEditingExpense(expenseItem);
    setFormData({
      amount: expenseItem.amount.toString(),
      currency_id: expenseItem.currency_id.toString(),
      description: expenseItem.description || '',
      create_date: new Date(expenseItem.create_date).toISOString().slice(0, 16)
    });
    setShowModal(true);
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      const response = await apiService.expenses.delete(expenseId);
      if (response.data.status === 'success') {
        setSuccess('Expense deleted successfully!');
        loadData();
      } else {
        setError(response.data.message || 'Delete failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingExpense(null);
    setFormData({
      amount: '',
      currency_id: '',
      description: '',
      create_date: new Date().toISOString().slice(0, 16)
    });
    setError('');
  };

  const formatAmount = (amount, currency = 'PLN') => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalExpenses = () => {
    return expenses.reduce((total, expense) => total + parseFloat(expense.amount_in_pln), 0);
  };

  // Vienna-specific expense suggestions with proper Unicode characters
  const viennaExpenseSuggestions = [
    'Schönbrunn Palace tickets 🏰',
    'Coffee at Café Central ☕',
    'Dinner at Figlmüller (Schnitzel) 🍽️',
    'U-Bahn day ticket 🚊',
    'Belvedere Museum (Klimt exhibition) 🎨',
    'Hotel accommodation 🏨',
    'Naschmarkt food tour 🥨',
    'Vienna State Opera tickets 🎭',
    'Sachertorte at Hotel Sacher 🍰',
    'Lunch at local restaurant 🍴',
    'Souvenirs at Christmas market 🎁',
    'Taxi ride 🚕',
    'Airport transfer ✈️',
    'Traditional Viennese coffee house ☕',
    'Albertina Museum 🖼️',
    'St. Stephen\'s Cathedral tour ⛪',
    'Prater amusement park 🎡',
    'Hundertwasserhaus visit 🏘️',
    'Danube Tower observation deck 🗼',
    'Traditional Austrian dinner 🥩'
  ];

  if (loading) {
    return <LoadingSpinner text="Loading expenses data..." />;
  }

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh' }}>
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="text-white mb-0">
                  <i className="fas fa-minus-circle me-3"></i>
                  Expenses Management
                </h1>
                <p className="text-light opacity-75">Track your Vienna trip expenses</p>
              </div>
              <div className="d-flex align-items-center">
                <div className="text-white me-4 text-center">
                  <div className="h4 mb-0">{formatAmount(getTotalExpenses())}</div>
                  <small>Total Spent</small>
                </div>
                <Button 
                  className="vienna-btn-primary"
                  onClick={() => setShowModal(true)}
                >
                  <i className="fas fa-plus me-2"></i>
                  Add Expense
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {error && (
          <Row className="mb-4">
            <Col>
              <Alert variant="danger" onClose={() => setError('')} dismissible>
                {error}
              </Alert>
            </Col>
          </Row>
        )}

        {success && (
          <Row className="mb-4">
            <Col>
              <Alert variant="success" onClose={() => setSuccess('')} dismissible>
                {success}
              </Alert>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <Card className="vienna-card">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Expense Entries ({expenses.length})
                </h5>
              </Card.Header>
              <Card.Body>
                {expenses.length > 0 ? (
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Currency</th>
                        <th>Amount in PLN</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((expense) => (
                        <tr key={expense.id}>
                                                     <td>
                             <div>
                               {expense.description || 'No description'}
                               {(expense.description?.includes('Schönbrunn') || expense.description?.includes('🏰')) && <i className="fas fa-crown ms-2 text-warning" title="Palace visit"></i>}
                               {(expense.description?.includes('Coffee') || expense.description?.includes('☕') || expense.description?.includes('Café')) && <i className="fas fa-coffee ms-2 text-brown" title="Coffee"></i>}
                               {(expense.description?.includes('Museum') || expense.description?.includes('🎨') || expense.description?.includes('🖼️')) && <i className="fas fa-university ms-2 text-info" title="Museum"></i>}
                               {(expense.description?.includes('Opera') || expense.description?.includes('🎭')) && <i className="fas fa-music ms-2 text-primary" title="Opera/Theater"></i>}
                               {(expense.description?.includes('U-Bahn') || expense.description?.includes('🚊')) && <i className="fas fa-subway ms-2 text-success" title="Public transport"></i>}
                             </div>
                           </td>
                          <td className="amount-negative fw-bold">
                            {formatAmount(expense.amount, expense.currency_code)}
                          </td>
                          <td>
                            <span className="currency-badge">{expense.currency_code}</span>
                          </td>
                          <td className="amount-negative">
                            {formatAmount(expense.amount_in_pln)}
                          </td>
                          <td>{formatDate(expense.create_date)}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleEdit(expense)}
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(expense.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-5">
                    <i className="fas fa-receipt fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No expenses yet</h5>
                    <p className="text-muted">Start tracking your Vienna trip expenses</p>
                    <Button 
                      className="vienna-btn-primary"
                      onClick={() => setShowModal(true)}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Add First Expense
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Add/Edit Expense Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-minus-circle me-2"></i>
              {editingExpense ? 'Edit Expense' : 'Add Expense'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="What did you spend money on?"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  list="expense-suggestions"
                />
                <datalist id="expense-suggestions">
                  {viennaExpenseSuggestions.map((suggestion, index) => (
                    <option key={index} value={suggestion} />
                  ))}
                </datalist>
                <Form.Text className="text-muted">
                  Start typing to see Vienna-specific suggestions
                </Form.Text>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Amount *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Currency *</Form.Label>
                    <Form.Select
                      value={formData.currency_id}
                      onChange={(e) => setFormData({...formData, currency_id: e.target.value})}
                      required
                    >
                      <option value="">Select currency</option>
                      {currencies.map((currency) => (
                        <option key={currency.id} value={currency.id}>
                          {currency.code} {currency.is_master_currency && '(Master)'}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Date & Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.create_date}
                  onChange={(e) => setFormData({...formData, create_date: e.target.value})}
                />
              </Form.Group>

              {/* Quick expense buttons */}
              <div className="mb-3">
                <Form.Label>Quick Add (EUR)</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {[
                    { label: 'Coffee ☕', amount: 4.5, desc: 'Coffee at Café Central ☕' },
                    { label: 'Meal 🍽️', amount: 15, desc: 'Lunch at Figlmüller (Schnitzel) 🍽️' },
                    { label: 'Transport 🚊', amount: 2.4, desc: 'U-Bahn day ticket 🚊' },
                    { label: 'Museum 🎨', amount: 12, desc: 'Belvedere Museum (Klimt exhibition) 🎨' },
                    { label: 'Schönbrunn 🏰', amount: 16, desc: 'Schönbrunn Palace tickets 🏰' }
                  ].map((quick, index) => {
                    const eurCurrency = currencies.find(c => c.code === 'EUR');
                    return (
                      <Button
                        key={index}
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setFormData({
                          ...formData,
                          amount: quick.amount.toString(),
                          currency_id: eurCurrency?.id.toString() || '',
                          description: quick.desc
                        })}
                      >
                        {quick.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" className="vienna-btn-primary">
                <i className="fas fa-save me-2"></i>
                {editingExpense ? 'Update' : 'Add'} Expense
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </div>
  );
};

export default ExpensesPage; 